import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap border rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow,transform] duration-150 ease-mk active:scale-[0.97] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 shadow-[shadow:inset_0_1px_theme(colors.white/15%)] dark:shadow-[shadow:inset_0_1px_theme(colors.black/15%)]",
  {
    variants: {
      variant: {
        default:
          "bg-zinc-900 dark:text-zinc-950 dark:hover:bg-zinc-100 dark:bg-white text-white shadow hover:bg-zinc-900/90 border border-zinc-950",
        white:
          "bg-zinc-50 text-zinc-600 shadow hover:bg-zinc-50/90 hover:text-zinc-800 border border-zinc-950/10",
        destructive:
          "bg-red-600 text-destructive-foreground shadow-sm hover:bg-red-600/90 border-red-700/90",
        outline:
          "border shadow-sm border-zinc-950/10 text-zinc-950 dark:border-white/15 dark:text-white dark:bg-white/0 dark:hover:bg-white/5 hover:bg-zinc-950/5",
        ghost: "border-transparent dark:shadow-none shadow-none text-zinc-950 hover:bg-zinc-950/5 dark:text-white dark:hover:bg-white/10",
        // Text-styled variants read as links, and a link does not shrink.
        link: "text-primary underline-offset-4 hover:underline active:scale-100",
        orange: "border-transparent bg-orange-600 font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500",
        orangeGhost: "border-transparent dark:shadow-none shadow-none text-orange-600 hover:bg-orange-600/10",
        // The one accent: only on buttons that write passport data (Lab bulk edits).
        write: "border-transparent bg-orange-700 text-white hover:bg-orange-800 focus-visible:ring-2 focus-visible:ring-orange-700/30",
        border: 'bg-transparent border-gray-50 text-gray-50 border-2 shadow-none hover:bg-gray-100 hover:text-gray-900 shadow-lg hover:border-gray-100',
        orangeBorder: 'bg-transparent border-orange-500 text-orange-500 border-2 shadow-none hover:bg-orange-500 hover:text-orange-50 shadow-lg hover:border-orange-500',
        text: 'bg-transparent !text-lg w-fit border-transparent !py-0 !px-0 text-gray-900 hover:underline hover:bg-transparent shadow-none active:scale-100',
        orangeText: 'bg-transparent !text-lg w-fit border-transparent !py-0 !px-0 text-orange-500 hover:underline hover:bg-transparent shadow-none active:scale-100',
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        md: "h-9 px-5 py-3.5 text-sm",
        lg: "rounded-md px-4 py-2.5 text-md",
        xl: "h-12 rounded-md px-10",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    (<Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />)
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
